import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, MessageSquare, Clock, User, Volume2, VolumeX, Settings, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import MessageConsole from './MessageConsole';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default message load limit
const DEFAULT_MESSAGE_LOAD_LIMIT = 50;
const MESSAGE_LOAD_LIMIT_KEY = 'messageConsole_loadLimit';

// Get message load limit from localStorage
export const getMessageLoadLimit = () => {
  const saved = localStorage.getItem(MESSAGE_LOAD_LIMIT_KEY);
  if (saved) {
    const parsed = parseInt(saved, 10);
    if (!isNaN(parsed) && parsed >= 10 && parsed <= 200) {
      return parsed;
    }
  }
  return DEFAULT_MESSAGE_LOAD_LIMIT;
};

// Set message load limit to localStorage
export const setMessageLoadLimit = (limit) => {
  localStorage.setItem(MESSAGE_LOAD_LIMIT_KEY, limit.toString());
};

// Create notification sound using Web Audio API
const createNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    return () => {
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const now = audioContext.currentTime;
      
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator1.connect(gainNode);
      oscillator2.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator1.type = 'sine';
      oscillator2.type = 'sine';
      
      oscillator1.frequency.setValueAtTime(880, now);
      oscillator1.frequency.setValueAtTime(1046.5, now + 0.1);
      
      oscillator2.frequency.setValueAtTime(659.25, now);
      oscillator2.frequency.setValueAtTime(783.99, now + 0.1);
      
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.15, now + 0.05);
      gainNode.gain.linearRampToValueAtTime(0.1, now + 0.15);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.4);
      
      oscillator1.start(now);
      oscillator2.start(now);
      oscillator1.stop(now + 0.4);
      oscillator2.stop(now + 0.4);
    };
  } catch (e) {
    console.warn('Web Audio API not supported:', e);
    return null;
  }
};

const NotificationBell = ({ autoOpenWith = null, onAutoOpenHandled = null }) => {
  const { getAuthHeaders, isAuthenticated, runningUser, isSuperAdmin } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentMessages, setRecentMessages] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    return saved !== 'false';
  });
  
  // Settings panel state
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [messageLoadLimit, setMessageLoadLimitState] = useState(getMessageLoadLimit);
  
  // Message Console state
  const [isMessageConsoleOpen, setIsMessageConsoleOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const dropdownRef = useRef(null);
  const prevUnreadCountRef = useRef(0);
  const playNotificationSound = useRef(createNotificationSound());
  const isFirstLoadRef = useRef(true);

  // Handle auto-open from props (for Support flow)
  useEffect(() => {
    if (autoOpenWith && isAuthenticated) {
      // Find the user to open chat with
      const openChatWithUser = async () => {
        try {
          // If it's 'super_admin', find the super admin
          if (autoOpenWith === 'super_admin') {
            const response = await fetch(`${API_URL}/api/admin/super-admin`, {
              headers: getAuthHeaders()
            });
            if (response.ok) {
              const data = await response.json();
              if (data.data) {
                setSelectedCustomer(data.data);
                setIsMessageConsoleOpen(true);
                if (onAutoOpenHandled) onAutoOpenHandled();
              }
            }
          } else if (autoOpenWith === 'calendar_admin') {
            // For customers, get their calendar admin
            const response = await fetch(`${API_URL}/api/auth/me`, {
              headers: getAuthHeaders()
            });
            if (response.ok) {
              const data = await response.json();
              const adminId = data.created_by_admin_id;
              if (adminId) {
                const adminResponse = await fetch(`${API_URL}/api/admin/users/${adminId}`, {
                  headers: getAuthHeaders()
                });
                if (adminResponse.ok) {
                  const adminData = await adminResponse.json();
                  setSelectedCustomer(adminData);
                  setIsMessageConsoleOpen(true);
                  if (onAutoOpenHandled) onAutoOpenHandled();
                }
              }
            }
          } else {
            // Direct user ID provided
            const response = await fetch(`${API_URL}/api/admin/users/${autoOpenWith}`, {
              headers: getAuthHeaders()
            });
            if (response.ok) {
              const userData = await response.json();
              setSelectedCustomer(userData);
              setIsMessageConsoleOpen(true);
              if (onAutoOpenHandled) onAutoOpenHandled();
            }
          }
        } catch (err) {
          console.error('Error auto-opening chat:', err);
        }
      };
      openChatWithUser();
    }
  }, [autoOpenWith, isAuthenticated, getAuthHeaders, onAutoOpenHandled]);

  // Save sound preference
  useEffect(() => {
    localStorage.setItem('notificationSoundEnabled', soundEnabled.toString());
  }, [soundEnabled]);

  // Handle message load limit change
  const handleMessageLoadLimitChange = (newLimit) => {
    const limit = parseInt(newLimit, 10);
    if (!isNaN(limit) && limit >= 10 && limit <= 200) {
      setMessageLoadLimitState(limit);
      setMessageLoadLimit(limit);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setShowSettingsPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Play sound when unread count increases
  const playSound = useCallback(() => {
    if (soundEnabled && playNotificationSound.current) {
      try {
        playNotificationSound.current();
      } catch (e) {
        console.warn('Could not play notification sound:', e);
      }
    }
  }, [soundEnabled]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await fetch(`${API_URL}/api/messages/unread/count`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        const newCount = data.unread_count || 0;
        
        if (!isFirstLoadRef.current && newCount > prevUnreadCountRef.current) {
          playSound();
        }
        
        isFirstLoadRef.current = false;
        prevUnreadCountRef.current = newCount;
        setUnreadCount(newCount);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, [isAuthenticated, getAuthHeaders, playSound]);

  // Fetch recent unread messages for dropdown
  const fetchRecentMessages = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/messages/unread/by-sender`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecentMessages(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching recent messages:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Initial fetch and periodic updates
  useEffect(() => {
    if (isAuthenticated) {
      isFirstLoadRef.current = true;
      prevUnreadCountRef.current = 0;
      
      fetchUnreadCount();
      
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, runningUser?.id, fetchUnreadCount]);

  // Fetch recent messages when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      fetchRecentMessages();
    }
  }, [isDropdownOpen, fetchRecentMessages]);

  const handleBellClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleMessageClick = async (sender) => {
    setIsDropdownOpen(false);
    
    // Set the selected customer/user and open MessageConsole
    setSelectedCustomer({
      id: sender.sender_id,
      full_name: sender.sender_name,
      email: sender.sender_email,
      role: sender.sender_role
    });
    setIsMessageConsoleOpen(true);
  };

  const handleMessagesLoaded = () => {
    // Refresh unread count after messages are loaded (they get marked as read)
    setTimeout(() => {
      fetchUnreadCount();
      fetchRecentMessages();
    }, 500);
  };

  const handleCloseMessageConsole = () => {
    setIsMessageConsoleOpen(false);
    setSelectedCustomer(null);
    // Refresh counts when closing
    fetchUnreadCount();
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    setSoundEnabled(!soundEnabled);
    
    if (!soundEnabled && playNotificationSound.current) {
      try {
        playNotificationSound.current();
      } catch (e) {
        console.warn('Could not play test sound:', e);
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Bell Icon Button */}
        <button
          onClick={handleBellClick}
          className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
          data-testid="notification-bell"
          aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
        >
          <Bell className="w-6 h-6" />
          
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 animate-pulse">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Messages</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleSound}
                  className={`p-1.5 rounded-full transition-colors ${
                    soundEnabled 
                      ? 'text-yellow-600 hover:bg-yellow-100' 
                      : 'text-gray-400 hover:bg-gray-200'
                  }`}
                  title={soundEnabled ? 'Sound on - Click to mute' : 'Sound off - Click to enable'}
                  data-testid="sound-toggle"
                >
                  {soundEnabled ? (
                    <Volume2 className="w-4 h-4" />
                  ) : (
                    <VolumeX className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setShowSettingsPanel(!showSettingsPanel)}
                  className={`p-1.5 rounded-full transition-colors ${
                    showSettingsPanel 
                      ? 'text-yellow-600 bg-yellow-100' 
                      : 'text-gray-400 hover:bg-gray-200'
                  }`}
                  title="Message Settings"
                  data-testid="message-settings-toggle"
                >
                  <Settings className="w-4 h-4" />
                </button>
                {unreadCount > 0 && (
                  <span className="bg-red-100 text-red-600 text-xs font-semibold px-2 py-1 rounded-full">
                    {unreadCount} unread
                  </span>
                )}
              </div>
            </div>

            {/* Settings Panel */}
            {showSettingsPanel && (
              <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Message Settings</h4>
                  <button
                    onClick={() => setShowSettingsPanel(false)}
                    className="p-1 hover:bg-yellow-100 rounded-full"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs text-gray-600">
                    Messages to load (initial & per page)
                  </label>
                  <select
                    value={messageLoadLimit}
                    onChange={(e) => handleMessageLoadLimitChange(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    data-testid="message-load-limit-select"
                  >
                    <option value={10}>10 messages</option>
                    <option value={25}>25 messages</option>
                    <option value={50}>50 messages (default)</option>
                    <option value={100}>100 messages</option>
                    <option value={200}>200 messages</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Controls how many messages load initially and when scrolling to load older messages.
                  </p>
                </div>
              </div>
            )}

            {/* Messages List */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500 mx-auto"></div>
                  <p className="mt-2 text-sm">Loading...</p>
                </div>
              ) : recentMessages.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No unread messages</p>
                </div>
              ) : (
                <div>
                  {recentMessages.map((msg) => (
                    <button
                      key={msg.sender_id}
                      onClick={() => handleMessageClick(msg)}
                      className="w-full px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-left transition-colors"
                      data-testid={`message-sender-${msg.sender_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-800 truncate">
                              {msg.sender_name || msg.sender_email || 'Unknown'}
                            </span>
                            {msg.unread_count > 0 && (
                              <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                {msg.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate mt-0.5">
                            {msg.sender_email}
                          </p>
                          {msg.latest_message && (
                            <p className="text-xs text-gray-400 mt-1 flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatTime(msg.latest_message)}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer - Only show if there are messages */}
            {recentMessages.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-center text-xs text-gray-500">
                Click on a sender to view conversation
              </div>
            )}
          </div>
        )}
      </div>

      {/* Message Console - Opens when sender is clicked */}
      {selectedCustomer && (
        <MessageConsole
          customer={selectedCustomer}
          isOpen={isMessageConsoleOpen}
          onClose={handleCloseMessageConsole}
          onMessagesLoaded={handleMessagesLoaded}
        />
      )}
    </>
  );
};

export default NotificationBell;
